// src/dataLayer.ts
function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
}
function push(entry) {
  ensureDataLayer();
  if (typeof window === "undefined") return;
  if (Object.keys(entry).length === 0) {
    console.warn("[Analytics] Empty data layer entry ignored");
    return;
  }
  window.dataLayer.push(entry);
}
function pushEvent(eventName, data) {
  push({
    event: eventName,
    ...data
  });
}
function clearEcommerce() {
  push({ ecommerce: null });
}
function pushPageView(data) {
  pushEvent("page_view", {
    page_title: data?.page_title ?? document.title,
    page_location: data?.page_location ?? window.location.href,
    page_path: data?.page_path ?? window.location.pathname,
    page_referrer: data?.page_referrer ?? document.referrer,
    content_group: data?.content_group
  });
}
function pushUserProperties(properties) {
  push({
    event: "user_properties_update",
    userId: properties.user_id,
    customerId: properties.customer_id,
    tenantId: properties.tenant_id,
    userRole: properties.user_role,
    subscriptionTier: properties.subscription_tier,
    state: properties.state
  });
}
function pushViewItem(item, value, currency = "USD") {
  clearEcommerce();
  push({
    event: "view_item",
    ecommerce: {
      currency,
      value: value ?? item.price ?? 0,
      items: [item]
    }
  });
}
function pushAddToCart(items, value, currency = "USD") {
  clearEcommerce();
  push({
    event: "add_to_cart",
    ecommerce: {
      currency,
      value,
      items
    }
  });
}
function pushBeginCheckout(items, value, currency = "USD", coupon) {
  clearEcommerce();
  push({
    event: "begin_checkout",
    ecommerce: {
      currency,
      value,
      coupon,
      items
    }
  });
}
function pushPurchase(data) {
  if (!data.transaction_id) {
    console.error("[Analytics] Purchase event requires transaction_id");
    return;
  }
  if (!data.items || data.items.length === 0) {
    console.error("[Analytics] Purchase event requires at least one item");
    return;
  }
  clearEcommerce();
  push({
    event: "purchase",
    ecommerce: {
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
      tax: data.tax,
      shipping: data.shipping,
      coupon: data.coupon,
      items: data.items
    }
  });
}
function pushLead(data) {
  pushEvent("generate_lead", {
    lead_source: data.lead_source,
    value: data.value,
    currency: data.currency ?? "USD",
    form_id: data.form_id,
    form_name: data.form_name
  });
}
function pushConsentUpdate(consent2) {
  push({
    event: "consent_update",
    ...consent2
  });
}
function pushSignUp(method, userId) {
  pushEvent("sign_up", {
    method,
    userId
  });
}
function pushLogin(method, userId) {
  pushEvent("login", {
    method,
    userId
  });
}
function pushSearch(searchTerm) {
  pushEvent("search", {
    search_term: searchTerm
  });
}
function pushCustomEvent(eventName, data) {
  pushEvent(eventName, data);
}
function getDataLayer() {
  if (typeof window === "undefined") return [];
  return window.dataLayer || [];
}
function resetDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = [];
}
var dataLayer = {
  push,
  pushEvent,
  pushPageView,
  pushUserProperties,
  pushViewItem,
  pushAddToCart,
  pushBeginCheckout,
  pushPurchase,
  pushLead,
  pushConsentUpdate,
  pushSignUp,
  pushLogin,
  pushSearch,
  pushCustomEvent,
  clearEcommerce,
  getDataLayer,
  resetDataLayer
};

// src/ecommerce.ts
var GOOGLE_ADS_CONVERSION_ID = "AW-17836361643";
var GOOGLE_ADS_CONVERSION_LABEL = "JDV9CMS6i4wcEKuPhblC";
var SERVICE_CATEGORIES = {
  registered_agent: "Registered Agent Services",
  annual_report: "Annual Reports",
  compliance: "Compliance Services",
  formation: "Business Formation"
};
function createServiceItem(service) {
  return {
    item_id: service.id,
    item_name: service.name,
    item_category: SERVICE_CATEGORIES[service.type] ?? "Other Services",
    item_category2: service.state,
    item_variant: service.type,
    price: service.price / 100,
    // Convert cents to dollars
    quantity: service.quantity ?? 1
  };
}
function trackViewService(service) {
  const item = createServiceItem(service);
  dataLayer.pushViewItem(item, item.price, "USD");
}
function trackAddToCart(services) {
  const items = services.map(createServiceItem);
  const value = items.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0);
  dataLayer.pushAddToCart(items, value, "USD");
}
function trackBeginCheckout(services, coupon) {
  const items = services.map(createServiceItem);
  const value = items.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0);
  dataLayer.pushBeginCheckout(items, value, "USD", coupon);
}
function trackPurchase(order) {
  const items = order.services.map(createServiceItem);
  const purchaseData = {
    transaction_id: order.orderId,
    value: order.total / 100,
    // Convert cents to dollars
    currency: "USD",
    tax: order.tax ? order.tax / 100 : void 0,
    coupon: order.coupon,
    items
  };
  dataLayer.pushPurchase(purchaseData);
  if (GOOGLE_ADS_CONVERSION_LABEL && typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`,
      value: purchaseData.value,
      currency: "USD",
      transaction_id: purchaseData.transaction_id
    });
  }
}
function trackRenewal(subscription) {
  const items = subscription.services.map(createServiceItem);
  dataLayer.pushCustomEvent("subscription_renewal", {
    transaction_id: `renewal_${subscription.subscriptionId}_${Date.now()}`,
    subscription_id: subscription.subscriptionId,
    customer_id: subscription.customerId,
    value: subscription.total / 100,
    currency: "USD",
    items
  });
}
function trackCancellation(subscription) {
  dataLayer.pushCustomEvent("subscription_cancel", {
    subscription_id: subscription.subscriptionId,
    customer_id: subscription.customerId,
    cancellation_reason: subscription.reason,
    lost_value: subscription.value / 100,
    currency: "USD"
  });
}
function trackRefund(refund) {
  const items = refund.items?.map(createServiceItem);
  dataLayer.clearEcommerce();
  dataLayer.push({
    event: "refund",
    ecommerce: {
      transaction_id: refund.transactionId,
      value: refund.value / 100,
      currency: "USD",
      items
    },
    refund_reason: refund.reason
  });
}
var ecommerce = {
  createServiceItem,
  trackViewService,
  trackAddToCart,
  trackBeginCheckout,
  trackPurchase,
  trackRenewal,
  trackCancellation,
  trackRefund
};

// src/config.ts
var GA4_MEASUREMENT_IDS = {
  marketing: "G-FN0MPMEYX6",
  portal: "G-7GH09SCQ3P",
  admin: "G-7GH09SCQ3P",
  development: "G-FN0MPMEYX6"
};
var GTM_CONTAINER_IDS = {
  production: "GTM-NKWWM3ML",
  staging: "GTM-NKWWM3ML",
  development: "GTM-NKWWM3ML"
};
var GTM_SERVER_URLS = {
  production: "https://gtm.geauxfile.com",
  staging: "https://gtm-staging.geauxfile.com",
  development: ""
  // No server-side in dev
};
function getEnvironment() {
  if (typeof window === "undefined") return "development";
  const hostname = window.location.hostname;
  if (hostname === "geauxfile.com" || hostname === "www.geauxfile.com" || hostname === "portal.geauxfile.com" || hostname === "admin.geauxfile.com") {
    return "production";
  }
  if (hostname.includes("staging") || hostname.includes("preview")) {
    return "staging";
  }
  return "development";
}
function getSiteContext() {
  if (typeof window === "undefined") return "development";
  const hostname = window.location.hostname;
  if (hostname === "portal.geauxfile.com") {
    return "portal";
  }
  if (hostname === "admin.geauxfile.com") {
    return "admin";
  }
  if (hostname === "geauxfile.com" || hostname === "www.geauxfile.com") {
    return "marketing";
  }
  return "development";
}
function getGA4MeasurementId() {
  const site = getSiteContext();
  return GA4_MEASUREMENT_IDS[site] ?? GA4_MEASUREMENT_IDS["development"] ?? "";
}
function getGTMContainerId() {
  const env = getEnvironment();
  return GTM_CONTAINER_IDS[env] ?? GTM_CONTAINER_IDS["development"] ?? "";
}
function getGTMServerUrl() {
  const env = getEnvironment();
  return GTM_SERVER_URLS[env] ?? "";
}
var GA4_MEASUREMENT_ID = typeof window !== "undefined" ? getGA4MeasurementId() : "";
var GTM_CONTAINER_ID = typeof window !== "undefined" ? getGTMContainerId() : "";
var GTM_SERVER_URL = typeof window !== "undefined" ? getGTMServerUrl() : "";
function getAnalyticsConfig() {
  const env = getEnvironment();
  const gtm = {
    containerId: GTM_CONTAINER_IDS[env] ?? "",
    serverUrl: GTM_SERVER_URLS[env],
    dataLayerName: "dataLayer"
  };
  const site = getSiteContext();
  const ga4 = {
    measurementId: GA4_MEASUREMENT_IDS[site] ?? "",
    sendPageView: true,
    debugMode: env === "development"
  };
  return {
    gtm,
    ga4,
    environment: env,
    consent: {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied"
    }
  };
}

// src/gtm.ts
var DEFAULT_CONSENT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  security_storage: "granted"
};
function initGTM(config2, consent2) {
  if (typeof window === "undefined") return;
  const containerId = config2?.containerId ?? getGTMContainerId();
  if (!containerId) {
    console.warn("[Analytics] GTM Container ID not configured");
    return;
  }
  window.dataLayer = window.dataLayer || [];
  const consentState = { ...DEFAULT_CONSENT, ...consent2 };
  window.dataLayer.push({
    "gtm.consent_default": consentState
  });
  function gtag(...args) {
    window.dataLayer.push(args);
  }
  gtag("consent", "default", {
    ...consentState,
    wait_for_update: 500
    // Wait 500ms for CMP to load
  });
  window.dataLayer.push({
    "gtm.start": (/* @__PURE__ */ new Date()).getTime(),
    event: "gtm.js"
  });
}
function loadGTMScript(containerId) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const id = containerId ?? getGTMContainerId();
  if (!id) {
    console.warn("[Analytics] GTM Container ID not configured");
    return;
  }
  if (document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${id}"]`)) {
    return;
  }
  initGTM({ containerId: id });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
}
function loadGTMNoScript(containerId) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const id = containerId ?? getGTMContainerId();
  if (!id) return;
  if (document.querySelector(`noscript iframe[src*="googletagmanager.com/ns.html?id=${id}"]`)) {
    return;
  }
  const noscript = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

// src/capi.ts
async function hashData(data) {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function hashUserData(userData) {
  const hashed = {};
  if (userData.email) {
    hashed.em = await hashData(userData.email);
  }
  if (userData.phone) {
    hashed.ph = await hashData(userData.phone.replace(/\D/g, ""));
  }
  if (userData.firstName) {
    hashed.fn = await hashData(userData.firstName);
  }
  if (userData.lastName) {
    hashed.ln = await hashData(userData.lastName);
  }
  if (userData.city) {
    hashed.ct = await hashData(userData.city);
  }
  if (userData.state) {
    hashed.st = await hashData(userData.state);
  }
  if (userData.zipCode) {
    hashed.zp = await hashData(userData.zipCode);
  }
  if (userData.country) {
    hashed.country = await hashData(userData.country);
  }
  if (userData.clientIpAddress) {
    hashed.client_ip_address = userData.clientIpAddress;
  }
  if (userData.clientUserAgent) {
    hashed.client_user_agent = userData.clientUserAgent;
  }
  if (userData.fbc) {
    hashed.fbc = userData.fbc;
  }
  if (userData.fbp) {
    hashed.fbp = userData.fbp;
  }
  if (userData.externalId) {
    hashed.external_id = await hashData(userData.externalId);
  }
  return hashed;
}
function generateEventId(prefix = "evt") {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 11);
  return `${prefix}_${timestamp}_${random}`;
}
var CapiClient = class {
  serverUrl;
  constructor(serverUrl) {
    this.serverUrl = serverUrl ?? getGTMServerUrl();
  }
  /**
   * Send event to GTM Server
   */
  async sendEvent(event) {
    if (!this.serverUrl) {
      console.warn("[CAPI] Server URL not configured");
      return { success: false, error: "Server URL not configured" };
    }
    try {
      const hashedUserData = await hashUserData(event.userData);
      const payload = {
        client_id: hashedUserData.external_id ?? generateEventId("cid"),
        events: [
          {
            name: event.eventName,
            params: {
              event_id: event.eventId,
              event_time: event.eventTime,
              event_source_url: event.eventSourceUrl,
              action_source: event.actionSource,
              user_data: hashedUserData,
              ...event.customData
            }
          }
        ]
      };
      const response = await fetch(`${this.serverUrl}/mp/collect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      console.info("[CAPI] Event sent:", {
        eventName: event.eventName,
        eventId: event.eventId
      });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      console.error("[CAPI] Failed to send event:", message);
      return { success: false, error: message };
    }
  }
  /**
   * Send purchase event
   */
  async sendPurchase(params) {
    return this.sendEvent({
      eventName: "purchase",
      eventTime: Math.floor(Date.now() / 1e3),
      eventId: generateEventId("purchase"),
      eventSourceUrl: params.sourceUrl,
      actionSource: "website",
      userData: params.userData,
      customData: {
        value: params.value,
        currency: params.currency ?? "USD",
        orderId: params.orderId,
        contentIds: params.items.map((i) => i.id),
        contentType: "product",
        contents: params.items.map((i) => ({
          id: i.id,
          quantity: i.quantity,
          itemPrice: i.price
        })),
        numItems: params.items.reduce((sum, i) => sum + i.quantity, 0)
      }
    });
  }
  /**
   * Send lead event
   */
  async sendLead(params) {
    return this.sendEvent({
      eventName: "lead",
      eventTime: Math.floor(Date.now() / 1e3),
      eventId: generateEventId("lead"),
      eventSourceUrl: params.sourceUrl,
      actionSource: "website",
      userData: params.userData,
      customData: {
        value: params.value,
        currency: params.currency ?? "USD",
        lead_source: params.leadSource
      }
    });
  }
};
var capiClientInstance = null;
function getCapiClient() {
  if (!capiClientInstance) {
    capiClientInstance = new CapiClient();
  }
  return capiClientInstance;
}
var capi = {
  sendEvent: (event) => getCapiClient().sendEvent(event),
  sendPurchase: (params) => getCapiClient().sendPurchase(params),
  sendLead: (params) => getCapiClient().sendLead(params),
  generateEventId
};

// src/consent.ts
var CONSENT_DEFAULTS = {
  EU: {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied"
  },
  EEA: {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied"
  },
  UK: {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied"
  },
  "US-CA": {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted"
    // CCPA allows analytics by default
  },
  US: {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted"
  },
  GLOBAL: {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied"
  }
};
var EU_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "IS",
  "LI",
  "NO"
];
var CONSENT_STORAGE_KEY = "geauxfile_consent";
function getStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    console.warn("[Consent] Failed to read stored consent");
  }
  return null;
}
function storeConsent(consent2) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({
        ...consent2,
        timestamp: (/* @__PURE__ */ new Date()).toISOString()
      })
    );
  } catch {
    console.warn("[Consent] Failed to store consent");
  }
}
function clearStoredConsent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    console.warn("[Consent] Failed to clear stored consent");
  }
}
function detectRegion() {
  if (typeof Intl === "undefined") return "GLOBAL";
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone.startsWith("Europe/") || timezone === "Atlantic/Reykjavik" || timezone === "Atlantic/Canary") {
      if (timezone === "Europe/London" || timezone === "Europe/Belfast") {
        return "UK";
      }
      return "EU";
    }
    if (timezone.startsWith("America/")) {
      if (timezone === "America/Los_Angeles" || timezone === "America/San_Francisco") {
        return "US-CA";
      }
      return "US";
    }
    return "GLOBAL";
  } catch {
    return "GLOBAL";
  }
}
function getDefaultConsent() {
  const region = detectRegion();
  return CONSENT_DEFAULTS[region] ?? CONSENT_DEFAULTS.GLOBAL;
}
function initConsentDefaults() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag(...args) {
    window.dataLayer.push(args);
  }
  const storedConsent = getStoredConsent();
  if (storedConsent) {
    gtag("consent", "default", {
      ...storedConsent,
      wait_for_update: 500
    });
    return;
  }
  const defaultConsent = getDefaultConsent();
  gtag("consent", "default", {
    ...defaultConsent,
    wait_for_update: 500
  });
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    region: EU_COUNTRIES
  });
}
function updateConsent(consent2) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag(...args) {
    window.dataLayer.push(args);
  }
  gtag("consent", "update", consent2);
  storeConsent(consent2);
  window.dataLayer.push({
    event: "consent_update",
    consent_state: consent2,
    timestamp: (/* @__PURE__ */ new Date()).toISOString()
  });
  console.info("[Consent] Updated:", consent2);
}
function grantAllConsent() {
  updateConsent({
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted"
  });
}
function denyAllConsent() {
  updateConsent({
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied"
  });
}
function acceptNecessaryOnly() {
  updateConsent({
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted"
  });
}
function isAnalyticsAllowed() {
  const consent2 = getStoredConsent();
  return consent2?.analytics_storage === "granted";
}
function isAdvertisingAllowed() {
  const consent2 = getStoredConsent();
  return consent2?.ad_storage === "granted";
}
function hasConsentChoice() {
  return getStoredConsent() !== null;
}
var consent = {
  initConsentDefaults,
  updateConsent,
  grantAllConsent,
  denyAllConsent,
  acceptNecessaryOnly,
  getStoredConsent,
  storeConsent,
  clearStoredConsent,
  detectRegion,
  getDefaultConsent,
  isAnalyticsAllowed,
  isAdvertisingAllowed,
  hasConsentChoice,
  CONSENT_DEFAULTS,
  EU_COUNTRIES
};

// src/consentHandler.ts
function convertCMPConsent(cmpData) {
  const consent2 = {};
  if (typeof cmpData.analytics === "boolean") {
    consent2.analytics_storage = cmpData.analytics ? "granted" : "denied";
  }
  if (typeof cmpData.marketing === "boolean") {
    consent2.ad_storage = cmpData.marketing ? "granted" : "denied";
    consent2.ad_user_data = cmpData.marketing ? "granted" : "denied";
  }
  if (typeof cmpData.personalization === "boolean") {
    consent2.ad_personalization = cmpData.personalization ? "granted" : "denied";
  }
  if (cmpData.purposeConsents) {
    if (typeof cmpData.purposeConsents["1"] === "boolean") {
      consent2.functionality_storage = cmpData.purposeConsents["1"] ? "granted" : "denied";
    }
    const adPurposes = ["4", "7"].some((p) => cmpData.purposeConsents?.[p]);
    if (adPurposes !== void 0) {
      consent2.ad_storage = adPurposes ? "granted" : "denied";
    }
    const personalizationPurposes = ["5", "6"].some((p) => cmpData.purposeConsents?.[p]);
    if (personalizationPurposes !== void 0) {
      consent2.ad_personalization = personalizationPurposes ? "granted" : "denied";
    }
    const analyticsPurposes = ["8", "9"].some((p) => cmpData.purposeConsents?.[p]);
    if (analyticsPurposes !== void 0) {
      consent2.analytics_storage = analyticsPurposes ? "granted" : "denied";
    }
  }
  return consent2;
}
function handleCMPCallback(eventType, cmpData) {
  console.info("[ConsentHandler] CMP event:", eventType, cmpData);
  switch (eventType) {
    case "consent_given":
    case "consent_updated":
    case "preferences_saved":
      if (cmpData) {
        const consent2 = convertCMPConsent(cmpData);
        updateConsent(consent2);
      }
      break;
    case "consent_denied":
      updateConsent({
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: "denied"
      });
      break;
    case "consent_expired":
      if (typeof window !== "undefined") {
        window.dataLayer?.push({
          event: "consent_expired",
          timestamp: (/* @__PURE__ */ new Date()).toISOString()
        });
      }
      break;
    case "preferences_shown":
      if (typeof window !== "undefined") {
        window.dataLayer?.push({
          event: "consent_preferences_shown",
          timestamp: (/* @__PURE__ */ new Date()).toISOString()
        });
      }
      break;
  }
}
function registerCMPListeners() {
  if (typeof window === "undefined") return;
  if ("Cookiebot" in window) {
    const cookiebot = window.Cookiebot;
    cookiebot.onaccept = () => {
      handleCMPCallback("consent_given", {
        marketing: cookiebot.consent?.marketing,
        analytics: cookiebot.consent?.statistics,
        personalization: cookiebot.consent?.preferences,
        necessary: cookiebot.consent?.necessary
      });
    };
    cookiebot.ondecline = () => {
      handleCMPCallback("consent_denied");
    };
  }
  if ("OneTrust" in window) {
    const oneTrust = window.OneTrust;
    oneTrust.OnConsentChanged?.((data) => {
      const groups = data.detail ?? [];
      handleCMPCallback("consent_updated", {
        necessary: groups.includes("C0001"),
        analytics: groups.includes("C0002"),
        personalization: groups.includes("C0003"),
        marketing: groups.includes("C0004")
      });
    });
  }
  if ("__tcfapi" in window) {
    const tcfApi = window.__tcfapi;
    tcfApi("addEventListener", 2, (data, success) => {
      if (success && data.purpose?.consents) {
        const purposeConsents = {};
        Object.entries(data.purpose.consents).forEach(([key, value]) => {
          purposeConsents[key] = value;
        });
        handleCMPCallback("consent_updated", {
          tcString: data.tcString,
          gdprApplies: data.gdprApplies,
          purposeConsents
        });
      }
    });
  }
}
function initConsentHandler() {
  if (typeof window === "undefined") return;
  const existingConsent = getStoredConsent();
  if (existingConsent) {
    updateConsent(existingConsent);
  }
  registerCMPListeners();
  window.addEventListener("consent-update", ((event) => {
    handleCMPCallback("consent_updated", event.detail);
  }));
}
function showConsentPreferences() {
  if (typeof window === "undefined") return;
  if ("Cookiebot" in window) {
    window.Cookiebot.show?.();
    return;
  }
  if ("OneTrust" in window) {
    window.OneTrust.ToggleInfoDisplay?.();
    return;
  }
  window.dispatchEvent(new Event("show-consent-preferences"));
}
function shouldShowConsentBanner() {
  return !hasConsentChoice();
}
var consentHandler = {
  handleCMPCallback,
  convertCMPConsent,
  registerCMPListeners,
  initConsentHandler,
  showConsentPreferences,
  shouldShowConsentBanner
};

// src/metaPixel.ts
function getPixelId() {
  if (typeof window !== "undefined") {
    const viteEnv = globalThis.__VITE_ENV__;
    if (viteEnv?.VITE_META_PIXEL_ID) {
      return viteEnv.VITE_META_PIXEL_ID;
    }
    const config2 = window.__ANALYTICS_CONFIG__;
    if (config2?.metaPixelId) {
      return config2.metaPixelId;
    }
  }
  return "";
}
function initMetaPixel(config2) {
  if (typeof window === "undefined") return;
  const pixelId = config2?.pixelId ?? getPixelId();
  if (!pixelId) {
    console.warn("[MetaPixel] Pixel ID not configured");
    return;
  }
  if (typeof window.fbq === "function") {
    return;
  }
  const fbq = function(...args) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
  fbq("init", pixelId);
  if (config2?.autoPageView !== false) {
    fbq("track", "PageView");
  }
  console.info("[MetaPixel] Initialized:", pixelId);
}
function trackMetaEvent(eventName, params) {
  if (typeof window === "undefined" || !window.fbq) {
    console.warn("[MetaPixel] Not initialized");
    return;
  }
  window.fbq("track", eventName, params);
}
function trackMetaCustomEvent(eventName, params) {
  if (typeof window === "undefined" || !window.fbq) {
    console.warn("[MetaPixel] Not initialized");
    return;
  }
  window.fbq("trackCustom", eventName, params);
}
function trackMetaPageView() {
  trackMetaEvent("PageView");
}
function trackMetaLead(params) {
  trackMetaEvent("Lead", {
    value: params?.value,
    currency: params?.currency ?? "USD",
    content_name: params?.content_name
  });
}
function trackMetaViewContent(params) {
  trackMetaEvent("ViewContent", {
    content_ids: [params.contentId],
    content_name: params.contentName,
    content_type: params.contentType ?? "product",
    value: params.value,
    currency: "USD"
  });
}
function trackMetaAddToCart(params) {
  trackMetaEvent("AddToCart", {
    content_ids: [params.contentId],
    content_name: params.contentName,
    content_type: "product",
    value: params.value,
    currency: "USD",
    contents: [{ id: params.contentId, quantity: params.quantity ?? 1 }]
  });
}
function trackMetaInitiateCheckout(params) {
  trackMetaEvent("InitiateCheckout", {
    content_ids: params.contentIds,
    content_type: "product",
    value: params.value,
    currency: "USD",
    num_items: params.numItems
  });
}
function trackMetaPurchase(params) {
  trackMetaEvent("Purchase", {
    content_ids: params.contentIds,
    content_type: "product",
    value: params.value,
    currency: "USD",
    contents: params.contents.map((c) => ({
      id: c.id,
      quantity: c.quantity,
      item_price: c.itemPrice
    })),
    num_items: params.contents.reduce((sum, c) => sum + c.quantity, 0)
  });
}
function trackMetaCompleteRegistration(params) {
  trackMetaEvent("CompleteRegistration", {
    value: params?.value,
    currency: "USD",
    status: params?.status ?? true
  });
}
var metaPixel = {
  init: initMetaPixel,
  track: trackMetaEvent,
  trackCustom: trackMetaCustomEvent,
  pageView: trackMetaPageView,
  lead: trackMetaLead,
  viewContent: trackMetaViewContent,
  addToCart: trackMetaAddToCart,
  initiateCheckout: trackMetaInitiateCheckout,
  purchase: trackMetaPurchase,
  completeRegistration: trackMetaCompleteRegistration
};

// src/linkedinInsight.ts
function getPartnerId() {
  if (typeof window !== "undefined") {
    const viteEnv = globalThis.__VITE_ENV__;
    if (viteEnv?.VITE_LINKEDIN_PARTNER_ID) {
      return viteEnv.VITE_LINKEDIN_PARTNER_ID;
    }
    const config2 = window.__ANALYTICS_CONFIG__;
    if (config2?.linkedinPartnerId) {
      return config2.linkedinPartnerId;
    }
  }
  return "";
}
function initLinkedInInsight(config2) {
  if (typeof window === "undefined") return;
  const partnerId = config2?.partnerId ?? getPartnerId();
  if (!partnerId) {
    console.warn("[LinkedInInsight] Partner ID not configured");
    return;
  }
  if (window._linkedin_data_partner_ids) {
    return;
  }
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(partnerId);
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
  console.info("[LinkedInInsight] Initialized:", partnerId);
}
function getLintrk() {
  if (typeof window === "undefined") return null;
  if (!window.lintrk) {
    window.lintrk = function(action, data) {
      window.lintrk.q = window.lintrk.q ?? [];
      window.lintrk.q.push([action, data]);
    };
    window.lintrk.q = [];
  }
  return window.lintrk;
}
function trackLinkedInConversion(conversionId, params) {
  const lintrk = getLintrk();
  if (!lintrk) {
    console.warn("[LinkedInInsight] Not initialized");
    return;
  }
  lintrk("track", {
    conversion_id: conversionId,
    ...params
  });
}
function trackLinkedInLead(conversionId, value) {
  trackLinkedInConversion(conversionId, {
    value,
    currency: "USD"
  });
}
function trackLinkedInSignUp(conversionId, value) {
  trackLinkedInConversion(conversionId, {
    value,
    currency: "USD"
  });
}
function trackLinkedInPurchase(conversionId, value) {
  trackLinkedInConversion(conversionId, {
    value,
    currency: "USD"
  });
}
function getLinkedInNoScriptUrl(partnerId) {
  const id = partnerId ?? getPartnerId();
  if (!id) return "";
  return `https://px.ads.linkedin.com/collect/?pid=${id}&fmt=gif`;
}
var linkedinInsight = {
  init: initLinkedInInsight,
  trackConversion: trackLinkedInConversion,
  trackLead: trackLinkedInLead,
  trackSignUp: trackLinkedInSignUp,
  trackPurchase: trackLinkedInPurchase,
  getNoScriptUrl: getLinkedInNoScriptUrl
};

// src/webVitals.ts
import { onCLS, onFID, onLCP, onINP, onTTFB, onFCP } from "web-vitals";
var metricsCache = {};
function defaultMetricHandler(metric, config2) {
  metricsCache[metric.name] = metric.value;
  if (config2.debug) {
    console.info(`[WebVitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta
    });
  }
  if (config2.sendToDataLayer && typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "web_vitals",
      web_vital_name: metric.name,
      web_vital_value: metric.value,
      web_vital_rating: metric.rating,
      web_vital_delta: metric.delta,
      web_vital_id: metric.id
    });
  }
  if (config2.sendToRUM && typeof window !== "undefined" && window.cwr) {
    window.cwr("recordEvent", {
      type: "web_vitals",
      data: {
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta
      }
    });
  }
  if (config2.onMetric) {
    config2.onMetric(metric);
  }
}
function initWebVitals(config2 = {}) {
  if (typeof window === "undefined") return;
  const options = {
    sendToDataLayer: true,
    sendToRUM: true,
    debug: false,
    ...config2
  };
  const handler = (metric) => defaultMetricHandler(metric, options);
  onCLS(handler);
  onFID(handler);
  onLCP(handler);
  onINP(handler);
  onTTFB(handler);
  onFCP(handler);
  if (options.debug) {
    console.info("[WebVitals] Initialized");
  }
}
function getWebVitalsMetrics() {
  return { ...metricsCache };
}
function hasAllCoreWebVitals() {
  return metricsCache.CLS !== void 0 && metricsCache.LCP !== void 0 && (metricsCache.FID !== void 0 || metricsCache.INP !== void 0);
}
function getWebVitalsSummary() {
  const metrics = getWebVitalsMetrics();
  let rating = "unknown";
  if (hasAllCoreWebVitals()) {
    const lcpGood = (metrics.LCP ?? 0) <= 2500;
    const lcpPoor = (metrics.LCP ?? 0) > 4e3;
    const clsGood = (metrics.CLS ?? 0) <= 0.1;
    const clsPoor = (metrics.CLS ?? 0) > 0.25;
    const inpGood = (metrics.INP ?? metrics.FID ?? 0) <= 200;
    const inpPoor = (metrics.INP ?? metrics.FID ?? 0) > 500;
    if (lcpGood && clsGood && inpGood) {
      rating = "good";
    } else if (lcpPoor || clsPoor || inpPoor) {
      rating = "poor";
    } else {
      rating = "needs-improvement";
    }
  }
  return { metrics, rating };
}
function reportWebVitals() {
  if (typeof window === "undefined") return;
  const summary = getWebVitalsSummary();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "web_vitals_report",
    web_vitals_summary: summary.rating,
    web_vitals_lcp: summary.metrics.LCP,
    web_vitals_cls: summary.metrics.CLS,
    web_vitals_inp: summary.metrics.INP ?? summary.metrics.FID,
    web_vitals_ttfb: summary.metrics.TTFB,
    web_vitals_fcp: summary.metrics.FCP
  });
  if (window.cwr) {
    window.cwr("recordEvent", {
      type: "web_vitals_report",
      data: summary
    });
  }
}
var webVitals = {
  init: initWebVitals,
  getMetrics: getWebVitalsMetrics,
  getSummary: getWebVitalsSummary,
  report: reportWebVitals,
  hasAllCoreWebVitals
};

// src/errorTracking.ts
var DEFAULT_CONFIG = {
  sampleRate: 1,
  maxErrors: 100,
  ignorePatterns: [/ResizeObserver loop/i, /Script error/i],
  captureConsoleErrors: true,
  captureUnhandledRejections: true
};
var config = DEFAULT_CONFIG;
var errorCount = 0;
var sessionId;
function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}
function getContext() {
  return {
    sessionId,
    pageUrl: typeof window !== "undefined" ? window.location.href : void 0,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : void 0,
    timestamp: (/* @__PURE__ */ new Date()).toISOString()
  };
}
function shouldCapture(message) {
  if (Math.random() > (config.sampleRate ?? 1)) {
    return false;
  }
  if (errorCount >= (config.maxErrors ?? 100)) {
    return false;
  }
  if (config.ignorePatterns?.some((pattern) => pattern.test(message))) {
    return false;
  }
  return true;
}
async function sendError(error) {
  if (!config.endpoint) {
    console.warn("[ErrorTracking] No endpoint configured, logging locally:", error);
    return;
  }
  try {
    await fetch(config.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(error),
      keepalive: true
    });
  } catch (e) {
    console.error("[ErrorTracking] Failed to send error:", e);
  }
}
function captureError(error, customData) {
  const message = typeof error === "string" ? error : error.message;
  if (!shouldCapture(message)) {
    return;
  }
  errorCount++;
  const capturedError = {
    message,
    stack: typeof error === "object" ? error.stack : void 0,
    type: "manual",
    context: {
      ...getContext(),
      customData
    }
  };
  void sendError(capturedError);
}
function captureException(error, customData) {
  captureError(error, customData);
}
function setUser(userId) {
  if (typeof window !== "undefined") {
    window.__ERROR_TRACKING_USER__ = userId;
  }
}
var breadcrumbs = [];
function addBreadcrumb(message, data) {
  breadcrumbs.push({
    message,
    timestamp: (/* @__PURE__ */ new Date()).toISOString(),
    data
  });
  if (breadcrumbs.length > 20) {
    breadcrumbs.shift();
  }
}
function initErrorTracking(userConfig) {
  if (typeof window === "undefined") return;
  config = { ...DEFAULT_CONFIG, ...userConfig };
  sessionId = generateSessionId();
  window.onerror = (message, source, lineno, colno, error) => {
    const msg = typeof message === "string" ? message : "Unknown error";
    if (!shouldCapture(msg)) {
      return false;
    }
    errorCount++;
    const capturedError = {
      message: msg,
      stack: error?.stack,
      type: "error",
      source: source ?? void 0,
      lineno: lineno ?? void 0,
      colno: colno ?? void 0,
      context: {
        ...getContext(),
        customData: { breadcrumbs: [...breadcrumbs] }
      }
    };
    void sendError(capturedError);
    return false;
  };
  if (config.captureUnhandledRejections) {
    window.onunhandledrejection = (event) => {
      const message = event.reason?.message ?? String(event.reason);
      if (!shouldCapture(message)) {
        return;
      }
      errorCount++;
      const capturedError = {
        message,
        stack: event.reason?.stack,
        type: "unhandledrejection",
        context: {
          ...getContext(),
          customData: { breadcrumbs: [...breadcrumbs] }
        }
      };
      void sendError(capturedError);
    };
  }
  if (config.captureConsoleErrors) {
    const originalError = console.error;
    console.error = (...args) => {
      originalError.apply(console, args);
      const message = args.map((arg) => typeof arg === "object" ? JSON.stringify(arg) : String(arg)).join(" ");
      if (!shouldCapture(message)) {
        return;
      }
      errorCount++;
      const capturedError = {
        message,
        type: "console",
        context: getContext()
      };
      void sendError(capturedError);
    };
  }
  console.info("[ErrorTracking] Initialized with session:", sessionId);
}
function getErrorCount() {
  return errorCount;
}
function resetErrorCount() {
  errorCount = 0;
}
var errorTracking = {
  init: initErrorTracking,
  captureError,
  captureException,
  setUser,
  addBreadcrumb,
  getErrorCount,
  resetErrorCount
};
export {
  CapiClient,
  GA4_MEASUREMENT_ID,
  GTM_CONTAINER_ID,
  acceptNecessaryOnly,
  addBreadcrumb,
  capi,
  captureError,
  captureException,
  consent,
  consentHandler,
  dataLayer,
  denyAllConsent,
  ecommerce,
  errorTracking,
  generateEventId,
  getAnalyticsConfig,
  getErrorCount,
  getWebVitalsMetrics,
  getWebVitalsSummary,
  grantAllConsent,
  handleCMPCallback,
  hasConsentChoice,
  initConsentDefaults,
  initConsentHandler,
  initErrorTracking,
  initGTM,
  initLinkedInInsight,
  initMetaPixel,
  initWebVitals,
  isAdvertisingAllowed,
  isAnalyticsAllowed,
  linkedinInsight,
  loadGTMNoScript,
  loadGTMScript,
  metaPixel,
  push,
  pushCustomEvent,
  pushEvent,
  pushPageView,
  reportWebVitals,
  resetErrorCount,
  setUser,
  shouldShowConsentBanner,
  showConsentPreferences,
  trackAddToCart,
  trackBeginCheckout,
  trackLinkedInConversion,
  trackLinkedInLead,
  trackLinkedInPurchase,
  trackMetaEvent,
  trackMetaLead,
  trackMetaPageView,
  trackMetaPurchase,
  trackPurchase,
  trackViewService,
  updateConsent,
  webVitals
};
